import { ThemeProvider } from '@mui/material/styles';
import Cookie from 'js-cookie';
import { get, noop } from 'lodash';
import { ReactElement, Suspense } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { EXTENSION_SAVE_TOKEN } from './Common/Common.constants';
import { getDeviceId } from './Common/Common.utils';
import MicroApp from './Containers/MicroApp/MicroApp';
import {
	sendMessageToDoc360App,
	setExternalAppInfo,
} from './Containers/Routes/Routes.reducers';
import { AccessTokenResponse } from './Containers/Routes/Routes.types';
import { FLO_ACTIVE_TAB_EDIT } from './Pages/Flo/Flo.constants';
import {
	getElementLocalisations,
	setTranslationContext,
	setTranslationCredits,
} from './Pages/Flo/Flo.reducers';
import store from './store';
import { pushState } from './store/reducers/Routes.reducer';
import * as serviceWorker from './sw-register';
import theme from './theme';
import API from './utils/API';
import {
	abortCaptureScreenshot,
	checkScreenShare,
	invokeScreenShare,
} from './utils/screenCapture';
import { SendMessageToExtension } from './utils/SendMessageToExtension';

let ErrorBoundary = ({ children }: { children: ReactElement }) => children;

class FloikFlo extends HTMLElement {
	static observedAttributes = [];

	constructor() {
		// Always call super first in constructor
		super();
	}
}

if (!customElements.get('floik-flo-preview')) {
	customElements.define('floik-flo-preview', FloikFlo);
}

if (
	process.env.NODE_ENV === 'production' &&
	process.env.REACT_APP_DISABLE_ERROR_MONITORING !== 'true'
) {
	// Bugsnag.start({
	// 	// @ts-ignore
	// 	apiKey: process.env.REACT_APP_BUGSNAG_API_KEY || 'e8b4672548e46c9068526502226b3f7a',
	// 	plugins: [new BugsnagPluginReact()],
	// });
	// @ts-ignore
	// ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);
}

// @ts-ignore
window['$crisp'] = window['$crisp'] || [];

const currentDate = new Date();
const futureDate = new Date(currentDate.getTime() + 5 * 24 * 60 * 60 * 1000);
const deviceId = getDeviceId();
const isMicroApp = process.env.REACT_APP_BUILD_MODE === 'MICRO' || false;

const COOKIE_PARAMS = {
	expires: futureDate,
};

if (!window.floikInstance) {
	window.floikInstance = {};
}

// Debug accessor so Doc360 can inspect floPage state when the app runs as a
// micro-app in their page (no iframe, so window.store is otherwise unreachable).
(window as any).__FLOIK_STORE__ = store;

// Set up Doc360-facing methods that only need dispatch here, so they are
// available as soon as the Floik script loads — before React/App mounts.
// This removes the race where Doc360 called openFlo and got "not a function".
const floikDebugLog = (message: string, data?: any) => {
	if (typeof window !== 'undefined') {
		const w = window as any;
		w.__FLOIK_NEWFLO_LOGS = w.__FLOIK_NEWFLO_LOGS || [];
		w.__FLOIK_NEWFLO_LOGS.push({ time: Date.now(), level: 'log', message, data });
	}
};

let activeEditorFloId: string | null = null;

const openFlo = ({
	floId = '6718d45a27d2ce0b81e2edb7',
	viewMode,
	hideRightPanel,
	additionalParams,
	translationCredits,
	availableLanguages,
}: {
	floId: string;
	viewMode?: 'readonly' | 'edit';
	hideRightPanel?: boolean;
	additionalParams?: { isPageCategory: boolean; langCode: string };
	translationCredits?: { total?: number; available?: number; enabled?: boolean };
	availableLanguages?: string[];
}) => {
	floikDebugLog('openFlo called', { floId, viewMode, hideRightPanel });
	if (!floId) return;
	activeEditorFloId = viewMode === 'readonly' ? null : floId;
	if (!translationCredits && (!availableLanguages || availableLanguages.length === 0)) {
		store.dispatch(
			setTranslationContext({ translationCredits: null, availableLanguages: null })
		);
	}
	const mergedAdditionalParams = {
		...(additionalParams || {}),
		...(translationCredits ? { translationCredits } : {}),
		...(availableLanguages ? { availableLanguages } : {}),
	};
	let additionalParamsQuery;
	if (Object.keys(mergedAdditionalParams).length > 0) {
		// URL-safe base64 so qs.parse doesn't turn '+' into a space
		additionalParamsQuery = btoa(JSON.stringify(mergedAdditionalParams))
			.replace(/\+/g, '-')
			.replace(/\//g, '_')
			.replace(/=/g, '');
	}
	store.dispatch(
		pushState(
			`/flos/${floId}?mode=${FLO_ACTIVE_TAB_EDIT}${
				viewMode === 'readonly' ? '&readOnly=true' : ''
			}${hideRightPanel ? '&hideRightPanel=true' : ''}${
				additionalParamsQuery ? `&additionalParams=${additionalParamsQuery}` : ''
			}`
		)
	);
};

window.floikInstance.openFlo = openFlo;

window.floikInstance.closeFlo = () => {
	floikDebugLog('closeFlo called');
	const floId = activeEditorFloId;
	activeEditorFloId = null;
	if (floId) {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		new API(undefined, false, false, false).post(
			`${API_BASE}/v1/flo/${encodeURIComponent(floId)}/status/re-publish`,
			{}
		).catch((error) => {
			floikDebugLog('Published output refresh failed', { floId, message: error?.message });
		});
	}
	store.dispatch(pushState('/doc360-empty'));
};

window.floikInstance.updateTranslationCredits = (
	credits: { total?: number; available?: number; enabled?: boolean } | null
) => {
	floikDebugLog('updateTranslationCredits called', { credits });
	store.dispatch(setTranslationCredits(credits));
};

window.floikInstance.updateTranslationContext = (context: {
	translationCredits?: { total?: number; available?: number; enabled?: boolean } | null;
	availableLanguages?: string[] | null;
}) => {
	floikDebugLog('updateTranslationContext called', { context });
	store.dispatch(setTranslationContext(context));
};

window.floikInstance.switchFloLanguage = ({
	floId,
	langCode,
	languageCode,
}: {
	floId: string;
	langCode?: string;
	languageCode?: string;
}) => {
	const selectedLanguage = langCode || languageCode;
	if (!floId || !selectedLanguage) return;
	store.dispatch(
		getElementLocalisations({ floId, languageCode: selectedLanguage })
	);
};

const getFloByExternalIntegrationId = async ({
	externalIntegrationId,
}: {
	externalIntegrationId: string;
}) => {
	if (!externalIntegrationId) return;

	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		const url = `${API_BASE}/v1/flo/${externalIntegrationId}/external`;

		// @ts-ignore
		const response: { data: AccessTokenResponse } = await new API(undefined).get(url);
		return {
			floId: get(response, 'data[0].id'),
			canUserOpenFlo: get(response, 'data[0].floStatus') !== 'CREATED',
		};
	} catch (error) {
		return '';
	}
};

const getFloByExternalAssetId = async ({
	externalAssetId,
}: {
	externalAssetId: string;
}) => {
	if (!externalAssetId) return;

	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		const url = `${API_BASE}/api/ext-integration/v1/flo/external-asset/${externalAssetId}`;

		// @ts-ignore
		const response: { data: AccessTokenResponse } = await new API(undefined).get(url);
		return {
			floId: get(response, 'data[0].id'),
			canUserOpenFlo: get(response, 'data[0].floStatus') !== 'CREATED',
		};
	} catch (error) {
		return '';
	}
};

const cloneFlo = async ({
	externalIntegrationId,
	floId,
	externalFloSpaceId,
	integrationScope,
	externalAssetId,
	newAssetId,
}: {
	externalIntegrationId?: string;
	floId: string;
	externalFloSpaceId?: string;
	integrationScope?: 'asset' | 'article';
	externalAssetId?: string;
	newAssetId?: string;
}) => {
	if (!floId) return;
	// Asset scope is explicit or implied when asset ids are passed; for article
	// scope, externalIntegrationId is required (backwards compatible).
	const isAssetScope =
		integrationScope === 'asset' ||
		integrationScope === 'project' ||
		Boolean(externalAssetId || newAssetId);
	if (!isAssetScope && !externalIntegrationId) return;

	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		// Asset-scope clones belong to no article; the backend accepts a new
		// externalAssetId query param to pre-tag the unbound clone.
		const url = isAssetScope
			? `${API_BASE}/v1/flo/${floId}/clone${newAssetId ? `?externalAssetId=${newAssetId}` : ''}`
			: `${API_BASE}/v1/flo/${floId}/clone?externalIntegrationId=${externalIntegrationId}`;

		const response = await new API(undefined).put(url, {});
		// @ts-ignore
		store.dispatch(
			sendMessageToDoc360App({
				type: 'floik_app_flo_clone',
				payload: {
					...(externalIntegrationId ? { externalIntegrationId } : {}),
					...(newAssetId ? { newAssetId } : {}),
					newFloId: get(response, 'data.id'),
					oldFloId: floId,
					integrationScope: isAssetScope ? 'asset' : 'article',
				},
			})
		);
	} catch (error) {
		store.dispatch(
			sendMessageToDoc360App({
				type: 'floik_app_flo_error',
				payload: {
					...(externalIntegrationId ? { externalIntegrationId } : {}),
					...(externalAssetId ? { externalAssetId } : {}),
					floId,
					errorType: 'FAILED_TO_CLONE_FLO',
				},
			})
		);
	}
};

const generateFloPreview = async ({ floId, languageCode }: { floId: string; languageCode?: string }) => {
	if (!floId) return;
	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		let url = `${API_BASE}/v1/flo/${floId}/preview`;
		if (languageCode) {
			url = `${url}?language=${languageCode}`;
		}

		// @ts-ignore
		const response = await new API(undefined).put(url);

		const status = get(response, 'data.floOutputMetadata[0].previewGenerationStatus');
		if (status === 'PREVIEW_GENERATED') {
			store.dispatch(
				sendMessageToDoc360App({
					type: 'floik_app_flo_preview_status',
					payload: {
						status: 'ready',
						floId,
					},
				})
			);
		} else if (status === 'PREVIEW_FAILED') {
			return {
				type: 'error',
				status: 'FAILED',
			};
		}
		return {
			status: 'GENERATING_PREVIEW',
		};
	} catch (error) {
		return error;
	}
};

const linkAssetToArticle = async ({
	assetId,
	articleId,
}: {
	assetId: string;
	articleId: string;
}) => {
	if (!assetId || !articleId) return;
	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		const url = `${API_BASE}/api/ext-integration/v1/flo/external-asset/${encodeURIComponent(
			assetId
		)}/external-link`;
		// @ts-ignore
		await new API(undefined).post(url, { articleId, externalAssetId: assetId });
		store.dispatch(
			sendMessageToDoc360App({
				type: 'floik_app_asset_linked',
				payload: { assetId, articleId },
			})
		);
	} catch (error) {
		store.dispatch(
			sendMessageToDoc360App({
				type: 'floik_app_flo_error',
				payload: { assetId, articleId, errorType: 'FAILED_TO_LINK_ASSET' },
			})
		);
	}
};

const unlinkAssetFromArticle = async ({
	assetId,
	articleId,
}: {
	assetId: string;
	articleId: string;
}) => {
	if (!assetId || !articleId) return;
	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		const url = `${API_BASE}/api/ext-integration/v1/flo/external-asset/${encodeURIComponent(
			assetId
		)}/external-unlink`;
		// @ts-ignore
		await new API(undefined).post(url, { articleId, externalAssetId: assetId });
		store.dispatch(
			sendMessageToDoc360App({
				type: 'floik_app_asset_unlinked',
				payload: { assetId, articleId },
			})
		);
	} catch (error) {
		store.dispatch(
			sendMessageToDoc360App({
				type: 'floik_app_flo_error',
				payload: { assetId, articleId, errorType: 'FAILED_TO_UNLINK_ASSET' },
			})
		);
	}
};

const login = async ({ externalAuthToken }: { externalAuthToken: string }) => {
	floikDebugLog('login called', { hasToken: !!externalAuthToken });
	if (!externalAuthToken) return;
	try {
		const API_BASE = process.env.REACT_APP_API_ENDPOINT || 'https://api.realmofdev.com';
		const url = `${API_BASE}/v1/external/authentication/token`;

		// @ts-ignore
		const response: { data: AccessTokenResponse } = await new API(undefined, true).post(
			url,
			{
				externalAuthToken,
				deviceToken: deviceId,
			}
		);

		if (get(response, 'data.authToken')) {
			SendMessageToExtension({
				type: EXTENSION_SAVE_TOKEN,
				payload: {
					token: get(response, 'data.authToken'),
				},
			});
			const authToken = get(response, 'data.authToken');
			const refreshToken = get(response, 'data.refreshToken');
			const userName = get(response, 'data.userName');
			const userId = get(response, 'data.userId');

			const prefix = isMicroApp ? 'micro-' : '';
			Cookie.set(`${prefix}floik-id`, userId || '', COOKIE_PARAMS);
			Cookie.set(`${prefix}floik-token-v2`, authToken || '', COOKIE_PARAMS);
			Cookie.set(`${prefix}floik-refreshToken-v2`, refreshToken || '', COOKIE_PARAMS);
			Cookie.set(`${prefix}floik-isRegistered`, 'true', COOKIE_PARAMS);
		}
		console.log('login response', response);
	} catch (error) {
		return error;
	}
};

let currentRoot: Root;

const renderApp = (
	microRootElement: Element | DocumentFragment,
	externalAppInfo?: {
		externalFloSpaceId: string;
	},
	renderCompleteCb?: Function
) => {
	const root = ReactDOMClient.createRoot(microRootElement);
	root.render(
		<ThemeProvider theme={theme}>
			<Provider store={store}>
				{/*{ isMicroApp ? <MicroApp /> : <App />}*/}
				<Suspense>
					<MicroApp
						externalAppInfo={externalAppInfo}
						renderCompleteCb={renderCompleteCb}
					/>
				</Suspense>
			</Provider>
		</ThemeProvider>
	);
	if (isMicroApp) {
		currentRoot = root;
	}
};

const linkElement = document.createElement('link');
linkElement.rel = 'stylesheet';
const devcssFile =
	(process.env.REACT_APP_DEPLOYMENT_ENV || 'dev') === 'dev'
		? 'main.a9f8aab6.css'
		: 'main.b4cb3eef.css';
linkElement.href =
	(process.env.REACT_APP_ENVIRONMENT || '') === 'production'
		? `https://micro-app.floik.com/static/css/main.61d34e15.css`
		: process.env.REACT_APP_URL
		? `${process.env.REACT_APP_URL.replace(/\/$/, '')}/static/css/main.c2606e04.css`
		: `https://micro-us.realmofdev.com/${
				process.env.REACT_APP_DEPLOYMENT_ENV || 'dev'
		  }/static/css/${devcssFile}`;
document.head.prepend(linkElement);
const linkElement2 = document.createElement('link');
linkElement2.rel = 'preconnect';
linkElement2.href = 'https://fonts.gstatic.com';
linkElement2.crossOrigin = 'true';
document.head.appendChild(linkElement2);
const linkElement3 = document.createElement('link');
linkElement3.rel = 'stylesheet';
linkElement3.href =
	'https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700;800&display=swap';
document.head.appendChild(linkElement3);

serviceWorker.register();

let curExternalAppInfo: {
	externalFloSpaceId?: string;
	externalIntegrationId?: string;
	externalAssetId?: string;
	integrationScope?: string;
} = {};

const unMountCurrentApp = () => {
	if (currentRoot) {
		// disconnectExtension();
		// window.floikInstance.resetSseEventSource();
		currentRoot.unmount();
	}
};

let renderMicroApp;

// Doc360 uses 'project' for the dashboard mount and 'asset' for asset-scoped
// creates; both mean the same thing to us. Canonicalize to 'asset' so every
// downstream reader (sagas, event payloads) sees one vocabulary.
const normalizeIntegrationScope = (scope?: string): 'asset' | 'article' | undefined => {
	if (scope === 'project' || scope === 'asset') return 'asset';
	if (scope === 'article') return 'article';
	return undefined;
};

renderMicroApp = (externalAppInfo: {
	externalFloSpaceId: string;
	externalIntegrationId?: string;
	externalAssetId?: string;
	integrationScope?: 'project' | 'asset' | 'article';
	planDetails?: { locked: boolean };
	externalRootElement?: Element | DocumentFragment;
}) => {
	externalAppInfo = {
		...externalAppInfo,
		integrationScope:
			normalizeIntegrationScope(externalAppInfo.integrationScope) ||
			(externalAppInfo.externalAssetId && !externalAppInfo.externalIntegrationId
				? 'asset'
				: undefined),
	};
	const hasfloSpaceIdChanged =
		externalAppInfo?.externalFloSpaceId !== curExternalAppInfo.externalFloSpaceId;
	const hasIntegrationIdChanged =
		externalAppInfo?.externalIntegrationId !== curExternalAppInfo.externalIntegrationId;
	const hasScopeChanged =
		externalAppInfo?.integrationScope !== curExternalAppInfo.integrationScope;
	const hasAssetIdChanged =
		externalAppInfo?.externalAssetId !== curExternalAppInfo.externalAssetId;
	// window.floikInstance = {
	// 	renderMicroApp: renderMicroApp,
	// 	login,
	// 	getFloByExternalIntegrationId,
	// 	generateFloPreview,
	// 	appRoot: {
	// 		unmount: noop
	// 	}
	// };
	if (hasfloSpaceIdChanged || hasIntegrationIdChanged || hasScopeChanged || hasAssetIdChanged) {
		floikDebugLog('renderMicroApp triggering remount', {
			externalAppInfo,
			hasfloSpaceIdChanged,
			hasIntegrationIdChanged,
			hasScopeChanged,
			hasAssetIdChanged,
		});
		// UNMOUNT resets redux including routes.current. If the host remounts while
		// a flo editor is open (e.g. Doc360 re-sends updateExternalAppInfo right
		// after openFlo), restore the route so the mounted editor isn't destroyed.
		const routeBeforeRemount = get(store.getState(), 'routes.current', '') as string;
		const prevExternalAppInfo = get(
			store.getState(),
			'routerContainer.externalAppInfo',
			{}
		) as Record<string, any>;
		const isFloRoute =
			!!routeBeforeRemount && routeBeforeRemount.split('?')[0].startsWith('/flos/');
		// Fields the host update omitted but the prior context had (e.g.
		// externalAssetId/integrationScope stored by an asset-scope createFlo)
		// are restored along with the flo route so outbound host messages keep
		// the correct asset context.
		const definedIncoming = Object.fromEntries(
			Object.entries(externalAppInfo).filter(([, v]) => v !== undefined)
		);
		const mergedExternalAppInfo = isFloRoute
			? { ...prevExternalAppInfo, ...definedIncoming }
			: externalAppInfo;
		const isMicroAppElementPresent = document.getElementById('microApp');
		if (isMicroAppElementPresent) {
			unMountCurrentApp();
			floikDebugLog('store UNMOUNT dispatched', { routeBeforeRemount });
			store.dispatch({ type: 'UNMOUNT' });
			// disconnectExtension();
			// window.floikInstance.resetSseEventSource();
			isMicroAppElementPresent.parentElement?.removeChild(isMicroAppElementPresent);
		}
		store.dispatch(setExternalAppInfo(mergedExternalAppInfo as any));
		const newElement = document.createElement('div');
		newElement.setAttribute('id', 'microApp');
		document.body.appendChild(newElement);
		curExternalAppInfo = externalAppInfo;
		renderApp(newElement, mergedExternalAppInfo);
		if (isFloRoute) {
			floikDebugLog('restoring route after remount', {
				route: routeBeforeRemount,
				mergedExternalAppInfo,
			});
			store.dispatch(pushState(routeBeforeRemount));
		}
	}
};

window.floikInstance.updateExternalAppInfo = (externalAppInfo: {
	externalFloSpaceId: string;
	externalIntegrationId?: string;
	integrationScope?: 'project' | 'asset' | 'article';
	externalAssetId?: string;
	planDetails?: { locked: boolean };
	externalRootElement?: Element | DocumentFragment;
}) => {
	renderMicroApp(externalAppInfo as any);
};
window.floikInstance.checkCaptureScreenshot = checkScreenShare;
window.floikInstance.abortCaptureScreenshot = abortCaptureScreenshot;
window.floikInstance.captureScreenshot = invokeScreenShare;
window.floikInstance.renderMicroApp = renderMicroApp;
window.floikInstance.login = login;
window.floikInstance.getFloByExternalIntegrationId = getFloByExternalIntegrationId;
window.floikInstance.getFloByExternalAssetId = getFloByExternalAssetId;
window.floikInstance.cloneFlo = cloneFlo;
window.floikInstance.linkAssetToArticle = linkAssetToArticle;
window.floikInstance.unlinkAssetFromArticle = unlinkAssetFromArticle;
window.floikInstance.generateFloPreview = generateFloPreview;
window.floikInstance.appRoot = {
	unmount: noop,
};

window.doc360Instance?.onMessageFromMicroApp({
	type: 'floik_script_loaded',
	payload: {
		loaded: true,
	},
});


///Development Render flo
if (
	isMicroApp &&
	process.env.NODE_ENV === 'development' &&
	process.env.REACT_APP_DEV_MODE === 'LOCAL'
) {
	// Get from doc 360 portal
	//https://portal.document360.net/externalFloSpaceId/document/v1/view/externalIntegrationId
	const devRoot = document.getElementById('root') || document.body;
	const externalFloSpaceId = '9f5c4e15-a5e9-4dc9-8a80-ba40727c773d';
	const externalIntegrationId = 'bd6cdc0f-c09e-4d1c-a723-27ffaa6dfe13';
	const floId = "6984717e1c7b5a10edc5c995";
	renderMicroApp({
		externalFloSpaceId,
		externalIntegrationId,
		externalRootElement: devRoot,
	});

	const openFloPayload = {
		"floId": floId,
		"viewMode": "edit",
		"hideRightPanel": false,
		"additionalParams": {
			"isPageCategory": false,
			"langCode": "en"
		}
	}
	if (floId && window?.floikInstance) {
		setTimeout(() => {
			window?.floikInstance?.openFlo?.(openFloPayload);
		}, 2000);
	}
}

//@ts-ignore
